import React, { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
    showAllQuery,
    useSearchQuestion,
    useSearchResults,
} from "../../context/DataContext";

import { SEARCH_RESULTS_SUMMARY_ID } from "../../constants/searchResultsSummary";
import { bemClassName } from "../../utils/bemClassName";
import { focusSearchResultsSummary } from "../../utils/focusSearchResultsSummary";
import { joinClassNames } from "../../utils/joinClassNames";
import styles from "../SearchContents/SearchContents.module.css";

const isRealQuery = (q: string): boolean =>
    !!q && q !== showAllQuery && q.trim() !== "";

export const ResultsSummary = () => {
    const summaryRef = useRef<HTMLDivElement>(null);
    const [searchParams] = useSearchParams();
    const {
        resultsSummary: summary,
        query: response,
        resultsFocusNonce,
    } = useSearchResults();
    const { query: searchQuery } = useSearchQuestion();

    useEffect(() => {
        if (resultsFocusNonce === 0) return;
        focusSearchResultsSummary(summaryRef.current);
    }, [resultsFocusNonce]);
    const resultName = summary.totalMatching === 1 ? "result" : "results";
    const fromContext = isRealQuery(searchQuery) ? searchQuery.trim() : "";
    const fromRouter = searchParams.get("query")?.trim() ?? "";
    const fromBrowser =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search)
                  .get("query")
                  ?.trim() ?? ""
            : "";
    const fromResponse = (response?.question?.query ?? "").trim();
    const displayQuery =
        (isRealQuery(fromContext) && fromContext) ||
        (isRealQuery(fromRouter) && fromRouter) ||
        (isRealQuery(fromBrowser) && fromBrowser) ||
        (isRealQuery(fromResponse) && fromResponse) ||
        null;
    const maybeQuery = displayQuery ? (
        <React.Fragment>
            {" "}
            for <strong>{displayQuery}</strong>
        </React.Fragment>
    ) : null;
    const maybeLastResult =
        summary.totalMatching > 1 ? (
            <React.Fragment>
                {" - "}
                <span id="search-page-end">{summary.currEnd}</span>
            </React.Fragment>
        ) : null;
    const maybeTotalResults =
        summary.totalMatching > 1 ? (
            <React.Fragment>
                {" "}
                of{" "}
                <span id="search-total-matching">{summary.totalMatching}</span>
            </React.Fragment>
        ) : null;
    return (
        <div
            ref={summaryRef}
            id={SEARCH_RESULTS_SUMMARY_ID}
            tabIndex={-1}
            className={joinClassNames([
                "text-muted",
                bemClassName("find-a-panel", "search-count"),
                styles.resultCount,
            ])}
        >
            Displaying <span id="search-page-start">{summary.currStart}</span>
            {maybeLastResult}
            {maybeTotalResults} {resultName}
            {maybeQuery}
        </div>
    );
};

export default ResultsSummary;
